import { EmptyState, FormCard } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Divider, Tag } from 'antd'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { getNominative } from 'common/utils'
import Description from '@lmc/ui-kit/src/dataDisplay/Descriptions/Description'
import { useSelector } from 'react-redux'
import {
  Home,
  IncidentReport,
  IncidentSeverityValues,
  FollowUpActionStatus,
} from '@lmc/ts-common'
import {
  getAdditionalItems,
  getDamagesItems,
  getDebriefingItems,
  getIncidentDetailsItems,
  getInjuriesItems,
  getLearningAndOutcomesItems,
  getMedicationsItems,
  getOpinionItems,
  getRestrictivePracticesItems,
} from '../../utils/viewCaseItems'
import { ListItem, NoDataIcon } from '@lmc/ui-kit/src/dataDisplay'
import useBodyMaps from 'hooks/useBodyMaps'

type IncidentCaseViewProps = {
  incidentReport: IncidentReport
  severities: IncidentSeverityValues | undefined
}

const labelStyle = { width: 270 }

export const IncidentCaseView = ({
  incidentReport,
  severities,
}: IncidentCaseViewProps) => {
  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { bodyMaps } = useBodyMaps({
    residentId: incidentReport.serviceUser.id,
  })

  const incidentDetailsItems = getIncidentDetailsItems(
    incidentReport,
    severities,
    getNominative(preferredPeopleNominative, 'Residents'),
    t
  )

  const restrictivePracticesItems = getRestrictivePracticesItems(
    incidentReport,
    t
  )

  const injuriesItems = getInjuriesItems(
    incidentReport,
    getNominative(preferredPeopleNominative),
    bodyMaps,
    t
  )

  const damagesItems = getDamagesItems(incidentReport, t)

  const medicationItems = getMedicationsItems(incidentReport, t)

  const opinionItems = getOpinionItems(incidentReport, t)

  const additionalItems = getAdditionalItems(incidentReport, t)

  const debriefingItems = getDebriefingItems(incidentReport, t)

  const learningAndOutcomesItems = getLearningAndOutcomesItems(
    incidentReport,
    t
  )

  const hasIncidentDetails =
    incidentDetailsItems.length > 0 ||
    restrictivePracticesItems.length > 0 ||
    injuriesItems.length > 0 ||
    damagesItems.length > 0 ||
    medicationItems.length > 0 ||
    additionalItems.length > 0 ||
    opinionItems.length > 0 ||
    learningAndOutcomesItems.length > 0

  const hasFollowUpActions =
    incidentReport?.case?.followUpActions?.actions &&
    incidentReport?.case?.followUpActions?.actions.length > 0

  const hasDebriefingItems = debriefingItems.length > 0

  const hasLearningAndOutcomes = learningAndOutcomesItems.length > 0

  const hasCaseInfo =
    hasIncidentDetails || hasFollowUpActions || hasDebriefingItems

  return (
    <Wrapper>
      <Title level={2}>{t(incidentReportsConstants.INCIDENT_CASE)}</Title>
      {hasIncidentDetails && (
        <FormCard title={t(incidentReportsConstants.INCIDENT_DETAILS)}>
          {incidentDetailsItems.length > 0 && (
            <Description
              labelStyle={labelStyle}
              items={incidentDetailsItems}
              bordered
              column={1}
            />
          )}
          {restrictivePracticesItems.length > 0 && (
            <SectionContainer>
              <FormCardHeading level={4}>
                {t(incidentReportsConstants.RESTRICTIVE_PRACTICES)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                labelStyle={labelStyle}
                items={restrictivePracticesItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}
          {injuriesItems.length > 0 && (
            <SectionContainer>
              <FormCardHeading level={4}>
                {t(incidentReportsConstants.INJURIES)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                labelStyle={labelStyle}
                items={injuriesItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}

          {damagesItems.length > 0 && (
            <SectionContainer>
              <FormCardHeading level={4}>
                {t(incidentReportsConstants.DAMAGES)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                labelStyle={labelStyle}
                items={damagesItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}

          {medicationItems.length > 0 && (
            <SectionContainer>
              <FormCardHeading level={4}>
                {t(incidentReportsConstants.MEDICATIONS)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                labelStyle={labelStyle}
                items={medicationItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}

          {additionalItems.length > 0 && (
            <SectionContainer>
              <FormCardHeading level={4}>
                {t(incidentReportsConstants.ADDITIONAL_COMMENT)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                labelStyle={labelStyle}
                items={additionalItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}

          {opinionItems.length > 0 && (
            <SectionContainer>
              <FormCardHeading level={4}>
                {t(incidentReportsConstants.SU_OPINION, {
                  variable: getNominative(preferredPeopleNominative),
                })}
              </FormCardHeading>
              <StyledDivider />
              <Description
                labelStyle={labelStyle}
                items={opinionItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}
        </FormCard>
      )}
      {hasFollowUpActions && (
        <ActionsFormCard title={t(incidentReportsConstants.FOLLOW_UP_ACTIONS)}>
          <ActionsList>
            {incidentReport.case?.followUpActions.actions.map((action) => (
              <ActionListItem
                title={<Text>{action.title}</Text>}
                rightChildren={
                  <Tag>
                    <Text>
                      {t(
                        incidentReportsConstants[
                          FollowUpActionStatus[action.status]
                        ]
                      ) ||
                        t(
                          incidentReportsConstants[
                            FollowUpActionStatus.notStarted
                          ]
                        )}
                    </Text>
                  </Tag>
                }
              />
            ))}
          </ActionsList>
        </ActionsFormCard>
      )}
      {hasDebriefingItems && (
        <FormCard title={t(incidentReportsConstants.DEBRIEFING)}>
          <Description
            labelStyle={labelStyle}
            items={debriefingItems}
            bordered
            column={1}
          />
        </FormCard>
      )}
      {hasLearningAndOutcomes && (
        <FormCard title={t(incidentReportsConstants.LEARNING_OUTCOMES)}>
          <Description
            labelStyle={labelStyle}
            items={learningAndOutcomesItems}
            bordered
            column={1}
          />
        </FormCard>
      )}
      {!hasCaseInfo && (
        <EmptyState
          icon={<NoDataIcon />}
          title={t(incidentReportsConstants.EMPTY_CASE_TITLE)}
          description={t(incidentReportsConstants.EMPTY_CASE_DESCRIPTION)}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-bottom: 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormCardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const ActionsFormCard = styled(FormCard)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ActionListItem = styled(ListItem)`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  padding: 24px 16px;
`

const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledDivider = styled(Divider)`
  color: ${({ theme }) => theme.color.neutral.color8};
  margin: 0px;
`

const Title = styled(Heading)``
